import { withNamespaces } from "react-i18next";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Row, UncontrolledTooltip } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { connect } from 'react-redux';
import { withRouter, Link } from "react-router-dom";
import PageBody from "src/modules/data-protection/components/page-body";
import Navigator from "src/components/Navigator";
import Spinner from "react-bootstrap/Spinner";
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import FilteringComponent from "./components/filtering-component";
import { 
    EMPTY_LIST,
    INTERNATIONAL_DATE_TIME_FORMAT
} from "src/common/constants";
import DocumentsService from "src/modules/data-protection/apis/DocumentsService";
import { 
    API_BASE_URL,
    ControlsFrameworks,
    DocumentsCategoryTypes, 
    DocumentsConfidentialityLevels, 
    DocumentSources, 
    DocumentsTypes 
} from "src/modules/data-protection/constants";
import NewDocumentModal from "./components/new-document-modal";
import axios from "axios";
import debounce from "lodash.debounce";
import DeleteDocumentModal from "./components/delete-document-modal";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from "src/common/constants";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";

const Documents = (props) => {
    const { t, token } = props;

    // const ismsControlsOptions = {
    //     tom_id: tomId
    // };

    const [ showNewDocumentModal, setShowNewDocumentModal ] = useState(false);
    const [ downloadsPercents, setDownloadsPercents ] = useState([]);
    const [ documentToDelete, setDocumentToDelete ] = useState(null);
    const [ documentToEdit, setDocumentToEdit ] = useState(null);

    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });

    const dateUtils = new DateUtils();

    const updatePercent = useRef(
        debounce(({
            id,
            percent
        }) => {
            setDownloadsPercents((currentState) => {
                return [...currentState].map((p) => {
                    if (p.id === id) {
                        return {
                            ...p,
                            percent: percent
                        }
                    }
                    return p;
                })
            }); 
        }, 100)
    ).current;

    const downloadFile = ({
        id,
        originName,
        size
    }) => {
        setDownloadsPercents((currentState) => {
            return [
                ...currentState,
                {
                    id: id,
                    percent: 0
                }
            ]
        });

        axios.get(`${API_BASE_URL}/asset_document/${id}/download`, {
            responseType: "arraybuffer",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            onDownloadProgress: (e) => {
                updatePercent({
                    id: id,
                    percent: Math.round((e.loaded * 100) / size)
                });
            },
        })
        .then((blob) => {
            const link = document.createElement("a");
        
            link.href = window.URL.createObjectURL(new Blob([blob.data]));
            link.setAttribute("download", originName);
        
            document.body.appendChild(link);
        
            link.click();
            link.remove();
        })
        .catch(() => {
            toast(t("An error occurred while downloading the file."), {
                type: "error"
            });
        })
        .finally(() => {
            setTimeout(() => {
                setDownloadsPercents((currentState) => {
                    return currentState.filter((p) => p.id !== id)
                });
            }, 500);
        });
    };

    const {
        data: documentsList = {},
        isFetching: documentsAreFetching,
        isFetched: documentsFetched,
        refetch: refetchDocumentsList
    } = useQuery({
        queryKey: [
            "data-protection-fetch-documents-list",
            tableFilters
        ],
        queryFn: async () => {
            const service = DocumentsService.getInstance();
            return await service.list(tableFilters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while retrieving the documents."), {
                type: "error"
            });
        },
    });

    const ismsAccessibleControlsQuery = useQuery({
        queryKey: [
            'gdpr-fetch-isms-accessible-controls'
        ],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchAccessibleControls({});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while retrieving the controls list.'), {
                type: 'error'
            });
        }
    });

    const NoDataIndication = () =>
        documentsFetched &&
            !documentsList.length ? (
            <div className="alert m-0" role="alert">
                <p className="text-center mb-0">
                    {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleTableChange = (type, data) => {
        switch (type) {
            case "pagination":
                const { page, sizePerPage } = data
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;

            case 'sort': {
                const { sortField, sortOrder } = data
                setTableFilters({
                    ...tableFilters,
                    sortField: sortField,
                    sortOrder: sortOrder,
                });
                break
            }
            default:
                return false;
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        setTableFilters((currentFilters) => {
            return {
                ...currentFilters,
                ...newFilters
            }
        });
    }, []);

    const breadCrumbs = useMemo(() => {
        return [
            {
                title: "SIP",
                link: `/admin/data-protection/dashboard`,
            },
            {
                title: "Documents",
                link: "#",
            }
        ];
    }, []);

    const {
        accessible_frameworks = []
    } = ismsAccessibleControlsQuery.data || {};

    const controlsColumns = accessible_frameworks?.map((accessibleFramework, index) => {
        const controleInfos = ControlsFrameworks.find((item) => item.key === accessibleFramework);

        if (!controleInfos) return undefined;

        return {
            key: (6 + index),
            dataField: "controls",
            text: t(controleInfos.title),
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                return (
                    <div>
                        {(cellContent[accessibleFramework] || []).map((c) => formatControlNumber(c.controlNumber, accessibleFramework)).join(', ')}
                    </div>
                )
            },
            sort: false
        }
    });

    let nextIndex = 6 + accessible_frameworks?.length;

    const columns = [
        {
            key: 0,
            dataField: "organization_secondary_id",
            text: t("ID"),
            sort: false,
            style: {
                width: '80px'
            }
        },
        {
            key: 1,
            dataField: "title",
            text: t("Title"),
            sort: true,
            style: {
                width: '180px'
            },
            formatter: (_cell, {
                title,
                originName,
                path,
                content_type
            }) => {
                if(content_type === 'link') {
                    return (
                        <a 
                            href={path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-break text-wrap text-truncate"
                        >
                            {title || originName}
                        </a>
                    )
                }

                return title || originName
            }
        },
        {
            key: 2,
            dataField: "type",
            text: t("Type"),
            sort: true,
            style: {
                width: '80px'
            },
            formatter: (cell) => {
                return t((DocumentsTypes.find((type) => type.value === cell))?.title)
            }
        },
        {
            key: 3,
            dataField: "source",
            text: t("Source"),
            sort: false,
            style: {
                width: '80px'
            },
            formatter: (cell, row) => {
                const source = cell || row?.source;

                if (!source) {
                    return '---';
                }

                return t((DocumentSources.find((item) => item.value === source))?.title || source);
            }
        },
        {
            key: 4,
            dataField: "category",
            text: t("Category"),
            sort: true,
            style: {
                width: '100px'
            },
            formatter: (cell) => {
                return t((DocumentsCategoryTypes.find((type) => type.value === cell))?.title || cell)
            }
        },
        {
            key: 5,
            dataField: "confidentialityLevel",
            text: t("Confidentiality Level"),
            sort: true,
            style: {
                width: '160px'
            },
            formatter: (cell) => {
                return t((DocumentsConfidentialityLevels.find((level) => level.value === cell))?.title)
            }
        },
        ...controlsColumns,
        {
            key: nextIndex++,
            dataField: "createdAt",
            text: t("Created On"),
            style: {
                width: '100px'
            },
            formatter: (cellContent) => {
                return (
                    <div>
                        {dateUtils.convertDateToDate(
                            cellContent,
                            INTERNATIONAL_DATE_FORMAT
                        )}
                    </div>
                )
            },
            sort: true
        },
        {
            key: nextIndex++,
            dataField: "created_by",
            text: t("Created By"),
            sort: false,
            style: {
                width: '140px'
            },
            formatter: (cell) => {
                return cell?.full_name || '---';
            }
        },
        {
            key: nextIndex++,
            dataField: "confluence_meta",
            text: t("Created By"),
            sort: false,
            style: {
                width: '140px'
            },
            formatter: (cell) => {
                return cell?.created_by?.full_name || '---';
            }
        },
        {
            key: nextIndex++,
            dataField: "confluence_meta",
            text: t("Last Synced"),
            sort: false,
            style: {
                width: '80px'
            },
            formatter: (cell) => {
                return cell?.synced_at ? dateUtils.convertDateToDate(cell.synced_at, INTERNATIONAL_DATE_TIME_FORMAT) : '---';
            }
        },
        {
            key: nextIndex++,
            dataField: "confluence_meta",
            text: t("Last Updated"),
            sort: false,
            style: {
                width: '80px'
            },
            formatter: (cell) => {
                return cell?.content_updated_at ? dateUtils.convertDateToDate(cell.content_updated_at, INTERNATIONAL_DATE_TIME_FORMAT) : '---';
            }
        },
        {
            key: nextIndex++,
            dataField: "actions",
            text: t("Actions"),
            sort: false,
            style: {
                width: '180px'
            },
            formatter: (_cell, row) => {
                const downloadPercent = downloadsPercents.find((percent) => percent.id === row.id);

                return (
                    <>
                        <>
                            <Button
                                id={`delete-document-${row.id}`}
                                color="link"
                                className="waves-effect me-1 text-danger"
                                onClick={() => setDocumentToDelete(row)}
                            >
                                <i className="ri-delete-bin-line font-size-22"></i>
                            </Button>
                            <UncontrolledTooltip
                                placement="top" 
                                target={`delete-document-${row.id}`}
                            >
                                {t('Delete Document')}
                            </UncontrolledTooltip>
                        </>

                        <>
                            <Button
                                id={`edit-document-${row.id}`}
                                color="primary"
                                outline
                                className="border-0 waves-effect me-1"
                                onClick={() => setDocumentToEdit(row)}
                            >
                                <i className="ri-pencil-line font-size-22"></i>
                            </Button>
                            <UncontrolledTooltip
                                target={`edit-document-${row.id}`}
                            >
                                {t('Edit Document')}
                            </UncontrolledTooltip>
                        </>

                        {row.content_type === 'file' ? (
                            <>
                                <Button
                                    id={`download-document-${row.id}`}
                                    color="link"
                                    className="waves-effect me-1"
                                    onClick={() => downloadFile(row)}
                                    disabled={downloadPercent}
                                >
                                    {downloadPercent ? (
                                        <>
                                            <Spinner 
                                                size="sm"
                                                color="primary"
                                                className="me-1"
                                            />
                                            {downloadPercent.percent}%
                                        </>
                                    ) : (
                                        <i className="ri-download-line font-size-22"></i>
                                    )}
                                </Button>
                                <UncontrolledTooltip
                                    placement="top" 
                                    target={`download-document-${row.id}`}
                                >
                                    {t('Download Document')}
                                </UncontrolledTooltip>
                            </>
                        ) : (
                            <>
                                <Button 
                                    id={`document-link-${row.id}`}
                                    color="link"
                                    href={row.path}
                                    target="_blank"
                                    tag="a"
                                    rel="noopener noreferrer"
                                >
                                    <i className="ri-external-link-line font-size-22"></i>
                                </Button>
                                <UncontrolledTooltip
                                    placement="top" 
                                    target={`document-link-${row.id}`}
                                >
                                    {t('View Document (opens in a new tab)')}
                                </UncontrolledTooltip>
                            </>
                        )}
                    </>
                )
            }
        }
    ];

    return (
        <div className="page-content">
            <NewDocumentModal
                isOpen={showNewDocumentModal || !!documentToEdit}
                toggle={() => {
                    setShowNewDocumentModal(false);
                    setDocumentToEdit(null);
                }}
                onComplete={refetchDocumentsList}
                documentToEdit={documentToEdit}
            />

            <DeleteDocumentModal 
                isOpen={!!documentToDelete}
                toggle={() => setDocumentToDelete(null)}
                onComplete={refetchDocumentsList}
                document={documentToDelete}
            />

            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle="Documents"
                            breadCrumbs={breadCrumbs}
                        />
                    </Col>
                </Row>

                <PageBody>
                    <div className="p-4" style={{ marginBottom: "300px" }}>
                        <PaginationProvider
                            pagination={paginationFactory({
                                custom: true,
                                page: tableFilters.pageIndex,
                                sizePerPage: tableFilters.pageSize,
                                totalSize: parseInt(documentsList?.itemsCount || 0),
                                withFirstAndLast: false,
                                alwaysShowAllBtns: true,
                                prePageText: (
                                    <span>
                                        <i className="ri-arrow-left-s-line"></i> {t("Back")}
                                    </span>
                                ),
                                nextPageText: (
                                    <span>
                                        {t("Next")} <i className="ri-arrow-right-s-line"></i>
                                    </span>
                                ),
                                prePageTitle: t("Pre page"),
                                firstPageTitle: t("Next page"),
                                sizePerPageList: [
                                    {
                                        text: "25",
                                        value: 25,
                                    },
                                    {
                                        text: "50",
                                        value: 50,
                                    },
                                ],
                                showTotal: false,
                                paginationTotalRenderer: (from, to, size) => {
                                    return (
                                        <span className="react-bootstrap-table-pagination-total">
                                            {`${props.t('Total Items')} ${size}`}
                                        </span>
                                    )
                                },
                                hideSizePerPage: false,
                            })}
                        >
                            {({ paginationProps, paginationTableProps }) => (
                                <React.Fragment>
                                    <Row className="mb-5 mt-3">
                                        <Col sm="12">
                                            <div className="d-flex justify-content-end">
                                                <FilteringComponent
                                                    onChange={handleFilterChange}
                                                />

                                                <Button
                                                    color="primary"
                                                    outline
                                                    disabled={documentsAreFetching}
                                                    onClick={() => setShowNewDocumentModal(true)}
                                                >
                                                    {t("Add Document")}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12">
                                            <BootstrapTable
                                                remote={{
                                                    pagination: true,
                                                    filter: false,
                                                    sort: true,
                                                    cellEdit: false,
                                                    search: false,
                                                }}
                                                loading={documentsAreFetching}
                                                overlay={overlayFactory({
                                                    spinner: (
                                                        <Spinner
                                                            animation="border"
                                                            variant="primary"
                                                            size="md"
                                                        />
                                                    ),
                                                    text: `${t('Loading')}...`,
                                                })}
                                                onTableChange={handleTableChange}
                                                defaultSorted={[]}
                                                keyField={"id"}
                                                responsive
                                                bordered={false}
                                                data={documentsList?.documents || []}
                                                striped={false}
                                                columns={columns}
                                                wrapperClasses="table-responsive"
                                                classes={"table tpdd-table"}
                                                headerWrapperClasses={"thead-light"}
                                                style={{
                                                    overflowX: "auto",
                                                }}
                                                noDataIndication={() => <NoDataIndication />}
                                                {...paginationTableProps}
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12">
                                            <div className="tpdd-pagination-style-1">
                                                <PaginationTotalStandalone
                                                    {...paginationProps}
                                                />

                                                <div>
                                                    <PaginationListStandalone
                                                        {...paginationProps}
                                                    />

                                                    <SizePerPageDropdownStandalone
                                                        {...paginationProps}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </React.Fragment>
                            )}
                        </PaginationProvider>
                    </div>
                </PageBody>
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
	const { token } = state.Login;

	return {
		token
	};
};

export default withNamespaces()(
    withRouter(
        connect(mapStatetoProps)(Documents)
    )
);
